import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: string;
  target: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_by: string;
  created_at: string;
}

const typeColors: Record<string, string> = {
  maintenance: 'bg-orange-100 text-orange-800',
  update: 'bg-blue-100 text-blue-800',
  news: 'bg-green-100 text-green-800',
};

const emptyForm = { title: '', message: '', type: 'update', target: 'all', starts_at: '', ends_at: '' };

export default function Announcements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => { fetchAnnouncements(); }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    try {
      const data = await api.get<{ announcements: Announcement[] }>('/announcements');
      setAnnouncements(data.announcements ?? []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function showSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  }

  function openEdit(a: Announcement) {
    setEditing(a);
    setForm({
      title: a.title,
      message: a.message,
      type: a.type,
      target: a.target,
      starts_at: a.starts_at ?? '',
      ends_at: a.ends_at ?? '',
    });
    setShowForm(true);
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await api.put(`/announcements/${editing.id}`, form);
        showSuccess('Aankondiging bijgewerkt');
      } else {
        await api.post('/announcements', form);
        showSuccess('Aankondiging aangemaakt');
      }
      setShowForm(false);
      await fetchAnnouncements();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(a: Announcement) {
    try {
      await api.put(`/announcements/${a.id}`, { is_active: !a.is_active });
      await fetchAnnouncements();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.delete(`/announcements/${id}`);
      showSuccess('Aankondiging verwijderd');
      await fetchAnnouncements();
    } catch (e: any) {
      setError(e.message);
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-500">Aankondigingen laden...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Aankondigingen</h1>
        <button onClick={openCreate} className="btn-primary">Nieuwe aankondiging</button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{success}</div>}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold">{editing ? 'Bewerk aankondiging' : 'Nieuwe aankondiging'}</h2>

            <div>
              <label className="label">Titel</label>
              <input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>

            <div>
              <label className="label">Bericht</label>
              <textarea className="input" rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Type</label>
                <select className="input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="maintenance">Onderhoud</option>
                  <option value="update">Update</option>
                  <option value="news">Nieuws</option>
                </select>
              </div>
              <div>
                <label className="label">Doelgroep</label>
                <select className="input" value={form.target} onChange={e => setForm({ ...form, target: e.target.value })}>
                  <option value="all">Iedereen</option>
                  <option value="plan_free">Free plan</option>
                  <option value="plan_pro">Pro plan</option>
                  <option value="plan_business">Business plan</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowForm(false)} className="btn-secondary">Annuleren</button>
              <button onClick={handleSubmit} disabled={saving || !form.title || !form.message} className="btn-primary">
                {saving ? 'Opslaan...' : editing ? 'Bijwerken' : 'Aanmaken'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements list */}
      <div className="space-y-3">
        {announcements.map(a => (
          <div key={a.id} className="card p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">{a.title}</h3>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[a.type] ?? 'bg-gray-100 text-gray-600'}`}>
                    {a.type}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${a.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {a.is_active ? 'Actief' : 'Inactief'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{a.message}</p>
                <div className="text-xs text-gray-400 mt-1">
                  Doelgroep: {a.target} | Aangemaakt: {new Date(a.created_at).toLocaleDateString('nl-NL')}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => toggleActive(a)} className="text-xs text-indigo-600 hover:text-indigo-800">
                  {a.is_active ? 'Deactiveer' : 'Activeer'}
                </button>
                <button onClick={() => openEdit(a)} className="text-xs text-gray-600 hover:text-gray-800">
                  Bewerk
                </button>
                <button onClick={() => handleDelete(a.id)} className="text-xs text-red-600 hover:text-red-800">
                  Verwijder
                </button>
              </div>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <div className="card p-8 text-center text-gray-500 text-sm">Nog geen aankondigingen</div>
        )}
      </div>
    </div>
  );
}
